import React from 'react';
import {
  IFontStyles,
  ITextProps,
  memoizeFunction,
  mergeStyles,
  Text as TextFluentUI, // eslint-disable-line no-restricted-imports
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import clsx from 'clsx';
import type { AppFontStyles, AppFontVariant } from 'common';
import { IAppTheme, isAppFontVariant } from 'common';
import { AppSemanticColors } from 'common/theme/AppSemanticColors';
import { mergeWith } from 'lodash';

type FluentTextProps = Omit<ITextProps, 'variant'>;

export interface IAppTextProps extends FluentTextProps {
  variant?: keyof IFontStyles | keyof AppFontStyles;
  maxLines?: number;
  color?: keyof AppSemanticColors;
  showTooltipOnOverflow?: boolean;
  /**
   * Props to politely announce text to a screen reader.
   * Useful for dialogs where heading is not immediately read aloud by a screen reader,
   * such as in cases with the first card in the onboarding card series.
   */
  announce?: boolean;
}

const getFontClassName = memoizeFunction(
  (theme: IAppTheme, variant?: AppFontVariant | keyof IFontStyles, role?: React.AriaRole, nowrap?: boolean, maxLines?: number) =>
    mergeStyles({
      ...(variant && isAppFontVariant(variant) ? theme.appFonts?.[variant] : undefined),
      ...(role === 'alert' && { color: theme.palette.redDark }),
      ...(maxLines && {
        display: '-webkit-box',
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
      }),
      ...(nowrap
        ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
        : { overflowWrap: 'break-word', wordBreak: 'break-word' }),
    })
);

export const Text: React.FunctionComponent<IAppTextProps> = (props: IAppTextProps) => {
  const { variant, className, style, maxLines, color, id, title, showTooltipOnOverflow, children, ...fluentProps } = props;
  const { role, nowrap } = fluentProps;
  const theme: IAppTheme = useTheme();
  const [showTooltipHost, setShowTooltipHost] = React.useState(false);
  const fallbackId = useId('text-');
  const tooltipId = useId('text-tooltip-');
  const textId = id || fallbackId;
  const fallbackTitle = typeof children === 'string' || typeof children === 'number' ? `${children}` : undefined;

  const renderText = React.useCallback(
    (titleOverride: string | undefined) => {
      return (
        <TextFluentUI
          {...fluentProps}
          variant={variant && isAppFontVariant(variant) ? undefined : variant}
          theme={theme}
          className={clsx(getFontClassName(theme, variant, role, nowrap, maxLines), className)}
          style={mergeWith({}, style, maxLines ? { WebkitLineClamp: maxLines } : {}, color ? { color: theme.appColors?.[color] } : {})}
          title={titleOverride}
          id={textId}
        >
          {children}
        </TextFluentUI>
      );
    },
    [fluentProps, variant, theme, role, nowrap, maxLines, className, style, color, textId, children]
  );

  React.useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    const elem = document.getElementById(textId);

    if (showTooltipOnOverflow && elem && (title ?? fallbackTitle)) {
      const checkOverflow = (element: Element) => {
        if (
          element.id === textId &&
          element.scrollHeight > 0 &&
          element.clientHeight > 0 &&
          element.scrollWidth > 0 &&
          element.clientWidth > 0
        ) {
          if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) {
            setShowTooltipHost(true);
          } else {
            setShowTooltipHost(false);
          }
        }
      };
      resizeObserver = new ResizeObserver((entries) => {
        const element = entries[0].target;
        checkOverflow(element);
      });

      checkOverflow(elem);
      resizeObserver.observe(elem);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [showTooltipOnOverflow, textId, title, showTooltipHost, fallbackTitle]);

  if (showTooltipHost) {
    return (
      <TooltipHost content={title ?? fallbackTitle} id={tooltipId} styles={{ root: { overflow: 'hidden', position: 'relative' } }}>
        {renderText(undefined)}
      </TooltipHost>
    );
  }

  return renderText(showTooltipOnOverflow ? undefined : title);
};
